/*
 * This file is part of glowingblue/orell-articles.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import type Mithril from 'mithril';
import Component, { type ComponentAttrs } from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import Tooltip from 'flarum/common/components/Tooltip';
import extractText from 'flarum/common/utils/extractText';
import ItemList from 'flarum/common/utils/ItemList';
import listItems from 'flarum/common/helpers/listItems';
import type Article from '../models/Article';
import WishlistActionButton from './WishlistActionButton';
import LiteraryWorkPageOrderButton from './LiteraryWorkPageOrderButton';
import app from 'flarum/forum/app';

export interface ArticleHeroAttrs extends ComponentAttrs {
	article: Article;
	articleDetailsItems: ItemList<Mithril.Children>;
	helperElement?: boolean;
}

export default class ArticleHero extends Component<ArticleHeroAttrs> {
	view() {
		const { article, helperElement } = this.attrs;
		return (
			<header className={`Hero ArticleHero ${helperElement ? 'helperElement' : ''}`}>
				<div className="container">
					<div className="sideNavContainer">
						<div className="ArticleHero-side--wrapper sideNav">
							<div className="ArticleHero-side--container">
								<div className="ArticleHero-cover">
									<img src={article.coverImage()} />
								</div>

								<div className="ArticlePage-Details--wrapper">{this.articleDetailsItems().toArray()}</div>
							</div>
						</div>
						<div className="ArticleHero-items--wrapper sideNavOffset">
							<ul className="ArticleHero-items">{listItems(this.items().toArray())}</ul>
							<ul className="ArticleHero-items ArticleHero-controls">{listItems(this.controlItems().toArray())}</ul>
						</div>
					</div>
				</div>
			</header>
		);
	}

	items(): ItemList<Mithril.Children> {
		const items = new ItemList<Mithril.Children>();
		const { article } = this.attrs;
		const author = article.author();

		items.add(
			'title',
			<div className="ArticleHero-item-content-wrapper">
				<h2>
					{article.title()}
					<span className="BCQuote closing" />
				</h2>
			</div>,
			10
		);

		items.add(
			'author',
			<div className="ArticleHero-item-content-wrapper">
				<Tooltip text={extractText(app.translator.trans('glowingblue-orell-articles.forum.article.search_author', { author }))} position="bottom">
					<Link href={app.route.of_articles(author)}>{author}</Link>
				</Tooltip>
			</div>,
			8
		);

		if (article.subtitle()) {
			items.add('subtitle', <h3>{article.subtitle()}</h3>, 6);
		}

		return items;
	}

	articleDetailsItems(): ItemList<Mithril.Children> {
		return this.attrs.articleDetailsItems;
	}

	controlItems(): ItemList<Mithril.Children> {
		const items = new ItemList<Mithril.Children>();

		const { article } = this.attrs;

		if (app.forum.attribute<boolean>('canAddToWishlist')) {
			items.add('add-to-wishlist', <WishlistActionButton article={article} />);
		}

		items.add('order', <LiteraryWorkPageOrderButton article={article} />, 10);

		items.add('separator', <hr />, 9);

		return items;
	}
}
